import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Check, Star, Trash2, Folder,
  Inbox, Archive, MoreVertical, Plus, Save
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [selectedContact, setSelectedContact] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contactsRes, foldersRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/contact-folders`)
      ]);
      setContacts(contactsRes.data);
      setFolders(foldersRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/admin/contact-folders`, { name: newFolderName });
      setNewFolderName('');
      setIsCreateFolderOpen(false);
      fetchData();
      toast.success('Папка создана');
    } catch (error) {
      toast.error('Ошибка создания папки');
    }
  };

  const deleteFolder = async (folderId) => {
    if (window.confirm('Удалить папку? Сообщения будут перемещены во Входящие.')) {
      try {
        await axios.delete(`${API}/admin/contact-folders/${folderId}`);
        if (activeFolder === folderId) setActiveFolder('inbox');
        fetchData();
        toast.success('Папка удалена');
      } catch (error) {
        toast.error('Ошибка удаления папки');
      }
    }
  };

  const updateContact = async (id, data) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}`, data);
      setContacts(contacts.map(c => c.id === id ? { ...c, ...data } : c));
      if (selectedContact && selectedContact.id === id) {
        setSelectedContact({ ...selectedContact, ...data });
      }
      toast.success('Обновлено');
    } catch (error) {
      toast.error('Ошибка обновления');
    }
  };

  const deleteContact = async (id) => {
    if (window.confirm('Удалить сообщение навсегда?')) {
      try {
        await axios.delete(`${API}/admin/contacts/${id}`);
        setContacts(contacts.filter(c => c.id !== id));
        if (selectedContact && selectedContact.id === id) setSelectedContact(null);
        toast.success('Удалено');
      } catch (error) {
        toast.error('Ошибка удаления');
      }
    }
  };

  const filteredContacts = contacts.filter(c => {
    if (activeFolder === 'important') return c.is_important;
    return (c.folder_id || 'inbox') === activeFolder;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  };

  const getIcon = (iconName) => {
    const icons = { Inbox, Trash2, Star, Folder };
    const Icon = icons[iconName] || Folder;
    return <Icon size={18} />;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r p-4 flex flex-col h-screen sticky top-0">
        <div className="flex items-center gap-2 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <h1 className="text-xl font-bold">Сообщения</h1>
        </div>

        <div className="space-y-1 flex-1">
          {folders.map(folder => (
            <div key={folder.id} className="group flex items-center justify-between">
              <button
                onClick={() => {
                  setActiveFolder(folder.id);
                  setSelectedContact(null);
                }}
                className={`flex-1 flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${activeFolder === folder.id
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-gray-100 text-gray-700'
                  }`}
              >
                {getIcon(folder.icon)}
                {folder.name}
                {/* Show unread count for all folders */}
                {(() => {
                  const unreadCount = contacts.filter(c => !c.read && (c.folder_id || 'inbox') === folder.id).length;
                  return unreadCount > 0 ? (
                    <Badge variant="secondary" className="ml-auto bg-white/20 text-inherit">
                      {unreadCount}
                    </Badge>
                  ) : null;
                })()}
              </button>
              {!folder.is_system && (
                <button
                  onClick={() => deleteFolder(folder.id)}
                  className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-red-500"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        <Dialog open={isCreateFolderOpen} onOpenChange={setIsCreateFolderOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full mt-4">
              <Plus size={16} className="mr-2" />
              Новая папка
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Создать папку</DialogTitle>
            </DialogHeader>
            <Input
              placeholder="Название папки"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
            />
            <DialogFooter>
              <Button onClick={createFolder}>Создать</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Message List */}
      <div className="w-80 bg-white border-r overflow-y-auto h-screen">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-gray-700">
            {folders.find(f => f.id === activeFolder)?.name || 'Сообщения'}
          </h2>
        </div>
        <div className="divide-y">
          {filteredContacts.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              Нет сообщений
            </div>
          ) : (
            filteredContacts.map(contact => (
              <div
                key={contact.id}
                onClick={() => setSelectedContact(contact)}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedContact?.id === contact.id ? 'bg-blue-50 hover:bg-blue-50' : ''
                  } ${!contact.read ? 'bg-gray-50' : ''}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className={`font-medium truncate ${!contact.read ? 'text-black' : 'text-gray-600'}`}>
                    {contact.name}
                  </span>
                  <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                    {new Date(contact.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-sm text-gray-500 truncate mb-2">
                  {contact.message}
                </div>
                <div className="flex gap-2">
                  {contact.is_important && <Star size={12} className="fill-yellow-400 text-yellow-400" />}
                  {!contact.read && <div className="w-2 h-2 rounded-full bg-blue-500 mt-1" />}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Message Detail */}
      <div className="flex-1 bg-gray-50 h-screen overflow-y-auto">
        {selectedContact ? (
          <div className="p-8 max-w-3xl mx-auto">
            <Card>
              <CardContent className="p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">{selectedContact.subject || 'Без темы'}</h2>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span className="font-medium text-gray-900">{selectedContact.name}</span>
                      <span>&lt;{selectedContact.email}&gt;</span>
                      <span>{formatDate(selectedContact.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!selectedContact.read && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateContact(selectedContact.id, { read: true })}
                      >
                        <Check size={16} className="mr-2" />
                        Прочитано
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => updateContact(selectedContact.id, { is_important: !selectedContact.is_important })}
                    >
                      <Star
                        className={selectedContact.is_important ? "fill-yellow-400 text-yellow-400" : "text-gray-400"}
                      />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Folder size={20} className="text-gray-400" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {folders.filter(f => !f.is_system).map(folder => (
                          <DropdownMenuItem
                            key={folder.id}
                            onClick={() => updateContact(selectedContact.id, { folder_id: folder.id })}
                          >
                            Переместить в {folder.name}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuItem onClick={() => updateContact(selectedContact.id, { folder_id: 'inbox' })}>
                          Входящие
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateContact(selectedContact.id, { folder_id: 'trash' })}>
                          Корзина
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteContact(selectedContact.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={20} />
                    </Button>
                  </div>
                </div>

                {/* Dynamic Data Fields */}
                {selectedContact.data && Object.keys(selectedContact.data).length > 0 && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg grid grid-cols-2 gap-4">
                    {Object.entries(selectedContact.data).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-xs font-medium text-gray-500 uppercase">{key}</span>
                        <p className="text-sm">{value}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Message Body */}
                <div className="prose max-w-none mb-8 text-gray-800 whitespace-pre-wrap">
                  {selectedContact.message}
                </div>

                {/* Notes Section */}
                <div className="border-t pt-6">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Заметки администратора
                  </label>
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Добавить заметку..."
                      value={selectedContact.notes || ''}
                      onChange={(e) => setSelectedContact({ ...selectedContact, notes: e.target.value })}
                      className="min-h-[100px]"
                    />
                    <Button
                      onClick={() => updateContact(selectedContact.id, { notes: selectedContact.notes })}
                      className="self-end"
                    >
                      <Save size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            Выберите сообщение для просмотра
          </div>
        )}
      </div>
    </div >
  );
};

export default AdminContacts;